import { useMutation, useQueryClient } from '@tanstack/react-query';
import { advanceApplicationsQueryKey, availableAdvancesQueryKey } from './keys';
import type { AdvancesHttpRequest, DocumentAdvanceApplicationDto } from '../types';

export interface UseRemoveAdvanceParams {
  documentId: string;
  /** Endpoint base DELETE (`/documents/{id}/advances`); se le añade `/{applicationId}`. */
  removeAdvanceEndpoint: string;
  httpRequest: AdvancesHttpRequest;
}

interface RemoveMutationContext {
  previous?: DocumentAdvanceApplicationDto[];
}

/**
 * Quita una aplicación `RESERVED` (`DELETE /{id}/advances/{applicationId}`).
 *
 * Solo aplica a filas `RESERVED` (RB-5); el backend responde 409 (BV-AA-07) si
 * la aplicación está `CONFIRMED`/`REVERSED`. Usa optimistic remove + rollback e
 * invalida ambas claves de caché.
 */
export function useRemoveAdvance({
  documentId,
  removeAdvanceEndpoint,
  httpRequest,
}: UseRemoveAdvanceParams) {
  const queryClient = useQueryClient();
  const appsKey = advanceApplicationsQueryKey(documentId);
  const availKey = availableAdvancesQueryKey(documentId);

  return useMutation<void, Error, string, RemoveMutationContext>({
    mutationFn: async (applicationId) => {
      await httpRequest<void>('DELETE', `${removeAdvanceEndpoint}/${applicationId}`);
    },
    onMutate: async (applicationId) => {
      await queryClient.cancelQueries({ queryKey: appsKey });
      const previous = queryClient.getQueryData<DocumentAdvanceApplicationDto[]>(appsKey);

      queryClient.setQueryData<DocumentAdvanceApplicationDto[]>(appsKey, (prev) =>
        (prev ?? []).filter((app) => app.id !== applicationId),
      );

      return { previous };
    },
    onError: (_error, _applicationId, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(appsKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: appsKey });
      queryClient.invalidateQueries({ queryKey: availKey });
    },
  });
}
